import baseApi from 'services/api/base';
import getProductKey from 'helper/requests/getProductKey';
import {
  GetChartDataParams,
  GetChartDataResponse,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getChartData';
import {
  SavePreferenceParams,
  SavePreferenceResponse,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/savePreference';
import {
  DeletePreferenceParams,
  DeletePreferenceResponse,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/deletePreference';
import { TrackingForecastPreference } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/preference';
import {
  transformGetTimeFrequencies,
  transformGetModels,
  transformGetVariables,
  transformGetMembers,
  transformGetDates,
  transformListPreferences,
} from 'helper/Meteorology/Fields/TrackingForecast/DetailedView';
import {
  GetTimeFrequenciesParams,
  TimeFrequency,
} from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getTimeFrequencies';
import { GetModelsParams, Model } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/model';
import { GetVariablesParams, Variable } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/variable';
import { GetMembersParams, Member } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/member';
import { GetDatesParams } from 'types/Meteorology/Fields/TrackingForecast/DetailedView/getDates';
import { SUBPAGE_SUBMENU_OPTIONS } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';

/**
 * Retenção de cache das listas ESTRUTURAIS do painel (5 min, contra o default de
 * 60s do RTK Query).
 *
 * Abrir a Visão Detalhada dispara uma cascata em série — frequências → modelos →
 * variáveis → data-previsão → chart/data — porque cada endpoint recebe as seleções
 * anteriores como parâmetro. Com 60s, sair da tela e voltar (ou trocar de aba
 * depois de um tempo) repaga a cascata inteira do zero.
 *
 * Só entram aqui as listas de CONFIGURAÇÃO: quais frequências, modelos, variáveis
 * e membros existem. Isso não muda quando entra rodada nova — só quando o time de
 * meteorologia altera o catálogo.
 *
 * Deliberadamente FORA: `data-previsao` e `chart/data`. A lista de datas alimenta o
 * default "data de previsão mais recente"; segurá-la em cache faria a tela abrir
 * numa rodada velha logo depois de o backend publicar uma nova — isso seria mudar
 * regra de negócio, não otimizar. Os dois seguem no default de 60s.
 */
const STRUCTURAL_OPTIONS_CACHE_SECONDS = 300;

export const trackingForecastDetailedViewApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTrackingForecastTimeFrequencies: build.query<TimeFrequency[], GetTimeFrequenciesParams>({
      query: ({ submenu }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/frequencias-temporais/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
          },
        };
      },
      transformResponse: transformGetTimeFrequencies,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getTrackingForecastDetailedViewModels: build.query<Model[], GetModelsParams>({
      query: (params) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/modelos/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu: params.submenu,
            frequencia_temporal: params.timeFrequency,
            id_local: params.locationId,
            data_previsao: params.forecastDate,
          },
        };
      },
      transformResponse: transformGetModels,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getTrackingForecastDetailedViewVariables: build.query<Variable[], GetVariablesParams>({
      query: ({
        submenu,
        timeFrequency,
        modelIds,
        members,
        forecastDate,
        locationId,
      }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/variaveis/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
            frequencia_temporal: timeFrequency,
            id_modelos: modelIds,
            membros: members,
            data_previsao: forecastDate,
            id_local: locationId,
          },
        };
      },
      transformResponse: transformGetVariables,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getTrackingForecastDetailedViewMembers: build.query<Member[], GetMembersParams>({
      query: ({
        modelId,
      }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/membros/list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu: 'multi-membros',
            id_modelo: modelId,
          },
        };
      },
      transformResponse: transformGetMembers,
      keepUnusedDataFor: STRUCTURAL_OPTIONS_CACHE_SECONDS,
    }),
    getTrackingForecastDetailedViewDates: build.query<string[], GetDatesParams>({
      query: ({
        locationId,
        locationIds,
        members,
        modelId,
        modelIds,
        submenu,
        timeFrequency,
        variable,
      }) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/painel-options/submenu/data-previsao/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu,
            id_local: locationId,
            ids_locais: locationIds,
            frequencia_temporal: timeFrequency,
            id_modelo: modelId,
            id_modelos: modelIds,
            membros: members,
            variavel: variable,
          },
        };
      },
      transformResponse: transformGetDates,
    }),
    getTrackingForecastChartData: build.query<GetChartDataResponse, GetChartDataParams>({
      query: (params) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/chart/data/?product_key=${productKey}`,
          method: 'POST',
          body: {
            submenu: SUBPAGE_SUBMENU_OPTIONS[params.subpage],
            chart_type: params.chartType,
            frequencia_temporal: params.timeFrequency,
            variavel: params.variable,
            id_local: params.locationId,
            lst_locais: params.locationIds,
            lst_modelos: params.modelIds,
            lst_membros: params.memberIds,
            lst_datas_rodada: params.forecastDates,
          },
        };
      },
    }),
    saveTrackingForecastPreference: build.mutation<
    SavePreferenceResponse,
    SavePreferenceParams
    >({
      query: (params) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/preferencias/save/?product_key=${productKey}`,
          method: 'POST',
          body: {
            name: params.name,
            json_p: {
              submenu: SUBPAGE_SUBMENU_OPTIONS[params.subpage],
              ...params.preferenceParams,
            },
            popular: false,
          },
        };
      },
      invalidatesTags: ['TrackingForecastDetailedViewPreferences'],
    }),
    listTrackingForecastPreferences: build.query<
    TrackingForecastPreference[],
    void
    >({
      query: () => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/preferencias/get?product_key=${productKey}`,
          method: 'POST',
        };
      },
      transformResponse: transformListPreferences,
      providesTags: ['TrackingForecastDetailedViewPreferences'],
    }),
    deleteTrackingForecastPreference: build.mutation<
    DeletePreferenceResponse,
    DeletePreferenceParams
    >({
      query: (params) => {
        const productKey = getProductKey('meteorologia');
        return {
          url: `produtos/local-previsao/visao-detalhada/preferencias/delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            name: params.name,
          },
        };
      },
      invalidatesTags: ['TrackingForecastDetailedViewPreferences'],
    }),
  }),
});

export const {
  useGetTrackingForecastTimeFrequenciesQuery,
  useGetTrackingForecastDetailedViewModelsQuery,
  useGetTrackingForecastDetailedViewDatesQuery,
  useLazyGetTrackingForecastDetailedViewDatesQuery,
  useLazyGetTrackingForecastDetailedViewModelsQuery,
  useGetTrackingForecastDetailedViewVariablesQuery,
  useLazyGetTrackingForecastDetailedViewVariablesQuery,
  useGetTrackingForecastDetailedViewMembersQuery,
  useLazyGetTrackingForecastDetailedViewMembersQuery,
  useGetTrackingForecastChartDataQuery,
  useLazyGetTrackingForecastChartDataQuery,
  useSaveTrackingForecastPreferenceMutation,
  useListTrackingForecastPreferencesQuery,
  useDeleteTrackingForecastPreferenceMutation,
} = trackingForecastDetailedViewApi;
